import React, { useState, useEffect } from 'react';
import api from '../config/api';

const PRESET_MULTIPLIERS = [
  { label: '1.5x', value: 1.5 },
  { label: '2x', value: 2 },
  { label: '2.5x', value: 2.5 },
  { label: '3x', value: 3 }
];

const OvertimeModal = ({
  show,
  onClose,
  employees,
  defaultEmployeeId,
  defaultMonth,
  defaultYear,
  editEntry,
  onSaved
}) => {
  const [employeeId, setEmployeeId] = useState('');
  const [date, setDate] = useState('');
  const [type, setType] = useState('full_day');
  const [hours, setHours] = useState('');
  const [multiplier, setMultiplier] = useState(2);
  const [customMultiplier, setCustomMultiplier] = useState('');
  const [useCustomMultiplier, setUseCustomMultiplier] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEditing = !!editEntry;

  useEffect(() => {
    if (!show) return;

    if (editEntry) {
      setEmployeeId(editEntry.employee?._id || editEntry.employee || '');
      setDate(new Date(editEntry.date).toISOString().split('T')[0]);
      setType(editEntry.type);
      setHours(editEntry.hours || '');
      setReason(editEntry.reason || '');
      const preset = PRESET_MULTIPLIERS.find(p => p.value === editEntry.multiplier);
      if (preset) {
        setMultiplier(editEntry.multiplier);
        setUseCustomMultiplier(false);
        setCustomMultiplier('');
      } else {
        setUseCustomMultiplier(true);
        setCustomMultiplier(String(editEntry.multiplier));
        setMultiplier(2);
      }
    } else {
      setEmployeeId(defaultEmployeeId || '');
      setType('full_day');
      setHours('');
      setMultiplier(2);
      setCustomMultiplier('');
      setUseCustomMultiplier(false);
      setReason('');
      setError('');

      if (defaultMonth && defaultYear) {
        const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'];
        const monthIndex = MONTHS.indexOf(defaultMonth);
        if (monthIndex !== -1) {
          const today = new Date();
          const inSameMonth = today.getMonth() === monthIndex && today.getFullYear() === defaultYear;
          const d = inSameMonth ? today : new Date(defaultYear, monthIndex, 1);
          setDate(d.toISOString().split('T')[0]);
        }
      } else {
        setDate(new Date().toISOString().split('T')[0]);
      }
    }
  }, [show, editEntry, defaultEmployeeId, defaultMonth, defaultYear]);

  const effectiveMultiplier = useCustomMultiplier
    ? parseFloat(customMultiplier) || 0
    : multiplier;

  const selectedEmployee = employees.find(e => e._id === employeeId);

  const previewAmount = (() => {
    if (!selectedEmployee || !effectiveMultiplier) return null;
    const dailyRate = selectedEmployee.salary / 30;
    const hourlyRate = dailyRate / 8;
    if (type === 'full_day') return dailyRate * effectiveMultiplier;
    const h = parseFloat(hours);
    if (h > 0) return hourlyRate * h * effectiveMultiplier;
    return null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!effectiveMultiplier || effectiveMultiplier <= 0) {
      setError('Please enter a valid multiplier');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        employee: employeeId,
        date,
        type,
        hours: type === 'hours' ? parseFloat(hours) : undefined,
        multiplier: effectiveMultiplier,
        reason
      };
      let result;
      if (isEditing) {
        const res = await api.put(`/api/overtime/${editEntry._id}`, payload);
        result = res.data;
      } else {
        const res = await api.post('/api/overtime', payload);
        result = res.data;
      }
      onSaved(result);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save overtime');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px', width: '95%' }}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Overtime' : 'Log Overtime'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && <div className="error" style={{ marginBottom: '12px' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Employee *</label>
            <select
              value={employeeId}
              onChange={e => setEmployeeId(e.target.value)}
              required
              disabled={isEditing}
            >
              <option value="">Select Employee</option>
              {employees.map(emp => (
                <option key={emp._id} value={emp._id}>{emp.name}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Type *</label>
              <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                <button
                  type="button"
                  onClick={() => setType('full_day')}
                  style={{
                    flex: 1, padding: '9px', border: '1px solid',
                    borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                    borderColor: type === 'full_day' ? '#4fc3f7' : '#444',
                    background: type === 'full_day' ? '#0d3a50' : '#3a3a3a',
                    color: type === 'full_day' ? '#4fc3f7' : '#ccc'
                  }}
                >
                  Full Day
                </button>
                <button
                  type="button"
                  onClick={() => setType('hours')}
                  style={{
                    flex: 1, padding: '9px', border: '1px solid',
                    borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                    borderColor: type === 'hours' ? '#4fc3f7' : '#444',
                    background: type === 'hours' ? '#0d3a50' : '#3a3a3a',
                    color: type === 'hours' ? '#4fc3f7' : '#ccc'
                  }}
                >
                  Hours
                </button>
              </div>
            </div>
          </div>

          {type === 'hours' && (
            <div className="form-group">
              <label>Hours Worked *</label>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={hours}
                onChange={e => setHours(e.target.value)}
                placeholder="e.g. 3.5"
                required
              />
            </div>
          )}

          <div className="form-group">
            <label>Multiplier *</label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
              {PRESET_MULTIPLIERS.map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => { setMultiplier(p.value); setUseCustomMultiplier(false); }}
                  style={{
                    padding: '7px 14px', border: '1px solid',
                    borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                    borderColor: (!useCustomMultiplier && multiplier === p.value) ? '#4caf50' : '#444',
                    background: (!useCustomMultiplier && multiplier === p.value) ? '#1a3a1a' : '#3a3a3a',
                    color: (!useCustomMultiplier && multiplier === p.value) ? '#4caf50' : '#ccc'
                  }}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setUseCustomMultiplier(true)}
                style={{
                  padding: '7px 14px', border: '1px solid',
                  borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                  borderColor: useCustomMultiplier ? '#f39c12' : '#444',
                  background: useCustomMultiplier ? '#3a2a00' : '#3a3a3a',
                  color: useCustomMultiplier ? '#f39c12' : '#ccc'
                }}
              >
                Custom
              </button>
            </div>
            {useCustomMultiplier && (
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={customMultiplier}
                onChange={e => setCustomMultiplier(e.target.value)}
                placeholder="e.g. 1.75"
                style={{ marginTop: '8px' }}
                required
              />
            )}
          </div>

          <div className="form-group">
            <label>Reason (optional)</label>
            <input
              type="text"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Client deadline, weekend work..."
            />
          </div>

          {previewAmount !== null && selectedEmployee && (
            <div style={{
              background: '#1a2a1a', border: '1px solid #2a5a2a',
              borderRadius: '8px', padding: '14px', marginBottom: '16px'
            }}>
              <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '6px' }}>Overtime Preview</div>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#888' }}>Daily Rate</div>
                  <div style={{ color: '#ccc', fontSize: '13px' }}>
                    PKR {(selectedEmployee.salary / 30).toFixed(2)}
                  </div>
                </div>
                {type === 'hours' && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#888' }}>Hourly Rate</div>
                    <div style={{ color: '#ccc', fontSize: '13px' }}>
                      PKR {(selectedEmployee.salary / 30 / 8).toFixed(2)}
                    </div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '11px', color: '#888' }}>Overtime Amount</div>
                  <div style={{ color: '#4caf50', fontSize: '16px', fontWeight: 'bold' }}>
                    PKR {previewAmount.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : isEditing ? 'Update' : 'Log Overtime'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OvertimeModal;
